import { MapContainer, TileLayer, Marker, Popup, Polyline, Tooltip, useMap, useMapEvents } from "react-leaflet";
import { useEffect, useState, useMemo, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { X, Users, Route as RouteIcon, MapPin, Pencil, UserPlus, Plus, Undo2, Loader2, Wand2 } from "lucide-react";

export const ROUTE_COLORS = ["#0020C2", "#DC2626", "#16A34A", "#D97706", "#7C3AED", "#0891B2", "#DB2777", "#65A30D", "#EA580C", "#0F766E"];

const stopIcon = (n, color) => L.divIcon({
  className: "custom-marker",
  html: `<div style="background:${color};color:#fff;width:24px;height:24px;border-radius:50%;border:2.5px solid white;box-shadow:0 1px 4px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;font-family:monospace;">${n}</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const unassignedIcon = L.divIcon({
  className: "custom-marker",
  html: `<div style="background:#fff;color:#D97706;width:22px;height:22px;border-radius:50%;border:2px dashed #D97706;box-shadow:0 1px 4px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:800;line-height:1;">+</div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const destIcon = L.divIcon({
  className: "custom-marker",
  html: `<div style="background:#0A0A0A;color:#fff;padding:3px 8px;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.4);font-size:10px;font-weight:800;letter-spacing:1px;white-space:nowrap;">HEDEF</div>`,
  iconSize: [52, 22],
  iconAnchor: [26, 11],
});

const draftIcon = (n) => L.divIcon({
  className: "custom-marker",
  html: `<div style="background:#16A34A;color:#fff;width:24px;height:24px;border-radius:50%;border:2.5px dashed white;box-shadow:0 0 0 3px rgba(22,163,74,.35);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;font-family:monospace;">${n}</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Çizim modunda haritaya tıklayınca durak ekleme
const ClickCapture = ({ enabled, onClick }) => {
  const map = useMap();
  useEffect(() => {
    if (!enabled) return;
    const el = map.getContainer();
    el.style.cursor = "crosshair";
    return () => { el.style.cursor = ""; };
  }, [enabled, map]);
  useMapEvents({ click(e) { if (enabled) onClick(e.latlng); } });
  return null;
};

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length > 1) map.fitBounds(L.latLngBounds(points), { padding: [30, 30] });
    else if (points.length === 1) map.setView(points[0], 13);
  }, [points, map]);
  return null;
};

// Firmanın TÜM güzergahlarını tek haritada gösterir — renk kodlu rotalar + personel isimleri
// onMove(fromRouteId, personnelId, toRouteId): haritadan güzergah değiştirme (müdahale)
export const CompanyRoutesMapModal = ({ company, routes, vehicleById = {}, driverById = {}, unassigned = [], onAdd = null, onCreateRoute = null, onMove = null, onStopMove = null, onRename = null, onClose }) => {
  const mapRoutes = useMemo(() => routes.filter((r) => (r.stops || []).length > 0), [routes]);
  const totalPeople = mapRoutes.reduce((a, r) => a + (r.personnel_ids?.length || 0), 0);
  const [showNames, setShowNames] = useState(totalPeople <= 60);
  const [showUnassigned, setShowUnassigned] = useState(true);
  const [hidden, setHidden] = useState(new Set());
  const [drawMode, setDrawMode] = useState(false);
  const [draftStops, setDraftStops] = useState([]); // [{lat, lng}]
  const [draftName, setDraftName] = useState("");
  const [creating, setCreating] = useState(false);
  const [selectedUn, setSelectedUn] = useState(new Set()); // atanmamışlardan seçim
  const mapRef = useRef(null);
  const markerRefs = useRef({});
  const mapBoxRef = useRef(null);

  const unassignedLocated = useMemo(
    () => unassigned.filter((p) => typeof p.lat === "number" && typeof p.lng === "number"),
    [unassigned]
  );

  // Listeden kişiye tıklayınca: haritada durağına uç + balonu aç
  const locatePerson = (routeId, stopIdx, lat, lng) => {
    if (hidden.has(routeId)) {
      setHidden(prev => { const s = new Set(prev); s.delete(routeId); return s; });
    }
    mapBoxRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => {
      mapRef.current?.flyTo([lat, lng], 16, { duration: 0.8 });
      setTimeout(() => markerRefs.current[`${routeId}-${stopIdx}`]?.openPopup(), 900);
    }, 150);
  };

  // Atanmamış kişiye haritada uç
  const locateUnassigned = (p) => {
    if (!showUnassigned) setShowUnassigned(true);
    mapBoxRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => {
      mapRef.current?.flyTo([p.lat, p.lng], 16, { duration: 0.8 });
      setTimeout(() => markerRefs.current[`u-${p.id}`]?.openPopup(), 900);
    }, 150);
  };

  const toggleRoute = (id) => {
    const s = new Set(hidden);
    if (s.has(id)) s.delete(id); else s.add(id);
    setHidden(s);
  };

  const toggleDrawMode = () => {
    setDrawMode(m => !m);
    setDraftStops([]);
    setDraftName("");
  };

  const toggleUnSelect = (id) => {
    setSelectedUn(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const createFromStops = async () => {
    if (draftStops.length === 0) return;
    setCreating(true);
    try {
      await onCreateRoute({ stops: draftStops, name: draftName.trim() || undefined });
      setDrawMode(false);
      setDraftStops([]);
      setDraftName("");
    } catch { /* hata bildirimi üstte gösterildi */ } finally { setCreating(false); }
  };

  const createFromSelected = async () => {
    if (selectedUn.size === 0) return;
    setCreating(true);
    try {
      await onCreateRoute({ personnel_ids: [...selectedUn] });
      setSelectedUn(new Set());
    } catch { /* hata bildirimi üstte gösterildi */ } finally { setCreating(false); }
  };

  const visible = mapRoutes.filter((r) => !hidden.has(r.id));
  const points = [
    ...visible.flatMap((r) => [
      ...(r.stops || []).map((s) => [s.lat, s.lng]),
      ...(r.dest ? [[r.dest.lat, r.dest.lng]] : []),
    ]),
    ...(showUnassigned ? unassignedLocated.map((p) => [p.lat, p.lng]) : []),
  ];
  // Aynı hedefi tek marker olarak göster
  const dests = [];
  visible.forEach((r) => {
    if (r.dest && !dests.some((d) => Math.abs(d.lat - r.dest.lat) < 1e-6 && Math.abs(d.lng - r.dest.lng) < 1e-6)) dests.push(r.dest);
  });

  return (
    <div className="fixed inset-0 bg-black/40 z-[1200] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-6xl rounded-md shadow-lg max-h-[94vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="company-routes-map-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-[600]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Firma Güzergah Haritası</div>
            <div className="font-heading font-bold text-lg">{company}</div>
            <div className="flex items-center gap-4 mt-1 text-xs text-neutral-600">
              <span className="flex items-center gap-1"><RouteIcon className="w-3.5 h-3.5 text-[#0020C2]" /> <b className="num">{mapRoutes.length}</b> güzergah</span>
              <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-[#0020C2]" /> <b className="num">{totalPeople}</b> personel</span>
              <label className="flex items-center gap-1.5 cursor-pointer select-none">
                <input type="checkbox" data-testid="company-map-names-toggle" checked={showNames} onChange={(e) => setShowNames(e.target.checked)} />
                İsimleri haritada göster
              </label>
              {unassigned.length > 0 && (
                <label className="flex items-center gap-1.5 cursor-pointer select-none text-[#D97706] font-semibold">
                  <input type="checkbox" data-testid="company-map-unassigned-toggle" checked={showUnassigned} onChange={(e) => setShowUnassigned(e.target.checked)} />
                  Atanmamışları göster (<span className="num">{unassigned.length}</span>)
                </label>
              )}
              {onStopMove && <span className="text-neutral-400">· Durakları sürükleyerek taşıyabilirsiniz</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onCreateRoute && (
              <button
                data-testid="map-draw-route-toggle"
                onClick={toggleDrawMode}
                title="Haritaya tıklayarak yeni güzergah durakları oluşturun"
                className={`flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-widest font-bold border transition-colors ${
                  drawMode ? "bg-[#16A34A] text-white border-[#16A34A]" : "border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                }`}
              >
                <Plus className="w-3.5 h-3.5" /> {drawMode ? "Çizim Modu Açık" : "Yeni Güzergah"}
              </button>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-[#F5F5F5]" data-testid="company-map-close"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Renk lejantı — tıklayınca rota gizlenir/gösterilir */}
        <div className="px-6 py-3 border-b border-[#F5F5F5] flex flex-wrap gap-2">
          {mapRoutes.map((r, i) => {
            const color = ROUTE_COLORS[i % ROUTE_COLORS.length];
            const off = hidden.has(r.id);
            const plate = vehicleById[r.vehicle_id]?.plate;
            const driver = driverById[r.driver_id]?.name;
            return (
              <button
                key={r.id}
                data-testid={`company-map-legend-${r.id}`}
                onClick={() => toggleRoute(r.id)}
                title={off ? "Rotayı göster" : "Rotayı gizle"}
                className={`flex items-center gap-1.5 px-2 py-1 text-[11px] border transition-opacity ${off ? "opacity-35 border-[#E5E5E5]" : "border-transparent"}`}
                style={off ? {} : { background: `${color}14`, borderColor: color }}
              >
                <span className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
                <span className="font-semibold">{r.name.replace(`${company} — `, "").slice(0, 38)}</span>
                {(plate || driver) && <span className="text-neutral-500">· {[plate, driver].filter(Boolean).join(" / ")}</span>}
                {r.estimated_km > 0 && <span className="num text-neutral-500">· {r.estimated_km} km</span>}
              </button>
            );
          })}
        </div>

        <div style={{ height: 520 }} ref={mapBoxRef} className="relative">
          <MapContainer ref={mapRef} center={points[0] || [39.9334, 32.8597]} zoom={11} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds points={points} />
            <ClickCapture enabled={drawMode} onClick={(ll) => setDraftStops(s => [...s, { lat: ll.lat, lng: ll.lng }])} />
            {/* Taslak duraklar (çizim modu) — tıklayınca kaldırılır */}
            {draftStops.map((s, i) => (
              <Marker
                key={`draft-${i}`}
                position={[s.lat, s.lng]}
                icon={draftIcon(i + 1)}
                eventHandlers={{ click: () => setDraftStops(prev => prev.filter((_, x) => x !== i)) }}
              >
                <Tooltip direction="top" offset={[0, -12]}><span style={{ fontSize: 10 }}>Kaldırmak için tıkla</span></Tooltip>
              </Marker>
            ))}
            {visible.map((r) => {
              const i = mapRoutes.findIndex((x) => x.id === r.id);
              const color = ROUTE_COLORS[i % ROUTE_COLORS.length];
              const line = (r.geometry || []).map(([lng, lat]) => [lat, lng]);
              return (
                <div key={r.id}>
                  {line.length > 1 && <Polyline positions={line} pathOptions={{ color, weight: 4, opacity: 0.75 }} />}
                  {(r.stops || []).map((s, si) => (
                    <Marker
                      key={`${r.id}-${si}`}
                      position={[s.lat, s.lng]}
                      icon={stopIcon(si + 1, color)}
                      ref={(el) => { if (el) markerRefs.current[`${r.id}-${si}`] = el; }}
                      draggable={Boolean(onStopMove)}
                      eventHandlers={onStopMove ? {
                        dragend: (e) => {
                          const ll = e.target.getLatLng();
                          if (window.confirm(`Durak ${si + 1} buraya taşınsın mı? (yola oturtulur, yürüme mesafeleri ve saatler yeniden hesaplanır)`)) {
                            onStopMove(r.id, si, ll.lat, ll.lng);
                          } else {
                            e.target.setLatLng([s.lat, s.lng]);
                          }
                        },
                      } : {}}
                    >
                      {showNames && (
                        <Tooltip permanent direction="right" offset={[10, 0]} className="route-name-tooltip">
                          <span style={{ fontSize: 10, fontWeight: 700 }}>{(s.personnel || []).map((p) => p.name).join(", ").slice(0, 60)}</span>
                        </Tooltip>
                      )}
                      <Popup maxWidth={300}>
                        <div className="text-xs" style={{ minWidth: 200 }}>
                          <div className="font-bold mb-1" style={{ color }}>{r.name.replace(`${company} — `, "")} · Durak {si + 1}</div>
                          {(s.personnel || []).map((p) => (
                            <div key={p.id} className="py-1 border-b border-neutral-100 last:border-0">
                              <div className="flex justify-between gap-3">
                                <span className="font-semibold">{p.name}{p.pickup_time ? ` (${p.pickup_time})` : ""}</span>
                                <span className="text-neutral-500 shrink-0">{p.walk_m} m yürüme</span>
                              </div>
                              {onMove && mapRoutes.length > 1 && (
                                <select
                                  data-testid={`map-move-${r.id}-${p.id}`}
                                  defaultValue=""
                                  onChange={(e) => { if (e.target.value) onMove(r.id, p.id, e.target.value); }}
                                  style={{ marginTop: 3, width: "100%", fontSize: 11, padding: "3px 4px", border: "1px solid #E5E5E5" }}
                                >
                                  <option value="">Güzergah değiştir →</option>
                                  {mapRoutes.filter((x) => x.id !== r.id).map((x, xi) => (
                                    <option key={x.id} value={x.id}>
                                      {x.name.replace(`${company} — `, "").slice(0, 40)}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                          ))}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </div>
              );
            })}
            {dests.map((d, i) => (
              <Marker key={`dest-${i}`} position={[d.lat, d.lng]} icon={destIcon}>
                <Popup><div className="text-xs font-semibold">{d.label || "Hedef"}</div></Popup>
              </Marker>
            ))}
            {/* Atanmamış personeller — turuncu kesikli + işaretçiler */}
            {showUnassigned && unassignedLocated.map((p) => (
              <Marker
                key={`u-${p.id}`}
                position={[p.lat, p.lng]}
                icon={unassignedIcon}
                ref={(el) => { if (el) markerRefs.current[`u-${p.id}`] = el; }}
              >
                {showNames && (
                  <Tooltip permanent direction="right" offset={[10, 0]} className="route-name-tooltip">
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#D97706" }}>{p.first_name} {p.last_name}</span>
                  </Tooltip>
                )}
                <Popup maxWidth={280}>
                  <div className="text-xs" style={{ minWidth: 190 }}>
                    <div className="font-bold mb-0.5" style={{ color: "#D97706" }}>Atanmamış Personel</div>
                    <div className="font-semibold">{p.first_name} {p.last_name}</div>
                    <div className="text-neutral-500 mb-1">{[p.district, p.address].filter(Boolean).join(" — ").slice(0, 80)}</div>
                    {onAdd && mapRoutes.length > 0 && (
                      <select
                        data-testid={`unassigned-add-${p.id}`}
                        defaultValue=""
                        onChange={(e) => { if (e.target.value) onAdd(p.id, e.target.value); }}
                        style={{ width: "100%", fontSize: 11, padding: "3px 4px", border: "1px solid #D97706" }}
                      >
                        <option value="">Güzergaha ekle →</option>
                        {mapRoutes.map((x) => (
                          <option key={x.id} value={x.id}>{x.name.replace(`${company} — `, "").slice(0, 40)}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
          {/* Çizim modu barı — haritaya tıklayarak durak ekle */}
          {drawMode && (
            <div
              data-testid="map-draw-bar"
              className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[500] bg-white border border-[#16A34A] shadow-lg px-3 py-2 flex items-center gap-2 flex-wrap max-w-[95%]"
            >
              <span className="text-xs font-bold whitespace-nowrap">
                Yeni güzergah: <span className="num text-[#16A34A]">{draftStops.length}</span> durak
              </span>
              <span className="text-[10px] text-neutral-400 hidden md:inline">— haritaya tıklayarak durak ekleyin</span>
              <input
                data-testid="map-draw-name"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                placeholder="Güzergah adı (opsiyonel)"
                className="text-xs border border-[#E5E5E5] px-2 py-1.5 w-[170px]"
              />
              <button
                data-testid="map-draw-undo"
                onClick={() => setDraftStops(s => s.slice(0, -1))}
                disabled={draftStops.length === 0}
                title="Son durağı geri al"
                className="flex items-center gap-1 px-2 py-1.5 text-[10px] uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5] disabled:opacity-40"
              >
                <Undo2 className="w-3 h-3" /> Geri Al
              </button>
              <button
                data-testid="map-draw-create"
                onClick={createFromStops}
                disabled={creating || draftStops.length === 0}
                className="flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold bg-[#16A34A] text-white hover:bg-[#0A0A0A] disabled:opacity-40"
              >
                {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} {creating ? "Oluşturuluyor..." : "Güzergahı Oluştur"}
              </button>
              <button data-testid="map-draw-cancel" onClick={toggleDrawMode} title="Çizimden çık" className="p-1.5 hover:bg-[#F5F5F5]">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Rota bazlı personel listesi */}
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Atanmamış personeller paneli */}
          {unassigned.length > 0 && (
            <div className="border border-dashed border-[#D97706] md:col-span-2" data-testid="unassigned-panel">
              <div className="px-3 py-2 text-xs font-bold flex items-center gap-2 flex-wrap" style={{ background: "#D9770614", borderBottom: "2px solid #D97706" }}>
                <UserPlus className="w-3.5 h-3.5 text-[#D97706]" />
                Atanmamış Personeller (<span className="num">{unassigned.length}</span>)
                <span className="font-normal text-neutral-500">— güzergah seçerek ekleyin; adresi yeni durak olur</span>
                {onCreateRoute && unassignedLocated.length > 0 && (
                  <span className="ml-auto flex items-center gap-2">
                    <label className="flex items-center gap-1 font-normal cursor-pointer select-none">
                      <input
                        type="checkbox"
                        data-testid="unassigned-select-all"
                        checked={selectedUn.size === unassignedLocated.length && unassignedLocated.length > 0}
                        onChange={(e) => setSelectedUn(e.target.checked ? new Set(unassignedLocated.map(p => p.id)) : new Set())}
                      />
                      Tümünü seç
                    </label>
                    <button
                      data-testid="unassigned-create-route"
                      onClick={createFromSelected}
                      disabled={creating || selectedUn.size === 0}
                      title="Seçilen personellerden yeni güzergah oluştur — duraklar, km ve saatler otomatik hesaplanır"
                      className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold bg-[#D97706] text-white hover:bg-[#0A0A0A] disabled:opacity-40"
                    >
                      {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                      Seçilenlerden Güzergah Oluştur ({selectedUn.size})
                    </button>
                  </span>
                )}
              </div>
              <div className="px-3 py-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0.5">
                {unassigned.map((p) => {
                  const located = typeof p.lat === "number" && typeof p.lng === "number";
                  return (
                    <div key={p.id} className="flex items-center gap-2 text-xs py-0.5">
                      {onCreateRoute && located && (
                        <input
                          type="checkbox"
                          data-testid={`unassigned-check-${p.id}`}
                          checked={selectedUn.has(p.id)}
                          onChange={() => toggleUnSelect(p.id)}
                          title="Yeni güzergah için seç"
                          className="shrink-0 accent-[#D97706]"
                        />
                      )}
                      <button
                        data-testid={`unassigned-locate-${p.id}`}
                        onClick={() => located && locateUnassigned(p)}
                        disabled={!located}
                        title={located ? "Haritada göster" : "Konumsuz — haritada gösterilemez"}
                        className={`flex items-center gap-1.5 min-w-0 flex-1 text-left ${located ? "hover:text-[#D97706] hover:underline" : "cursor-default"}`}
                      >
                        <span className="truncate font-medium">{p.first_name} {p.last_name}</span>
                        {located && <MapPin className="w-3 h-3 shrink-0 opacity-40" />}
                      </button>
                      <span className="text-neutral-400 truncate max-w-[140px] shrink-0">{p.district || ""}</span>
                      {!located && <span className="text-[9px] uppercase tracking-widest font-bold text-[#DC2626] shrink-0">Konumsuz</span>}
                      {located && onAdd && mapRoutes.length > 0 && (
                        <select
                          data-testid={`unassigned-list-add-${p.id}`}
                          defaultValue=""
                          onChange={(e) => { if (e.target.value) onAdd(p.id, e.target.value); }}
                          title="Bu personeli güzergaha ekle"
                          className="text-[10px] border border-[#D97706] bg-white px-1 py-0.5 cursor-pointer text-[#D97706] font-semibold shrink-0 max-w-[130px]"
                        >
                          <option value="">Ekle →</option>
                          {mapRoutes.map((x) => (
                            <option key={x.id} value={x.id}>{x.name.replace(`${company} — `, "").slice(0, 35)}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {mapRoutes.map((r, i) => {
            const color = ROUTE_COLORS[i % ROUTE_COLORS.length];
            return (
              <div key={r.id} className="border border-[#E5E5E5]" data-testid={`company-map-route-list-${r.id}`}>
                <div className="px-3 py-2 text-xs font-bold flex items-center gap-2" style={{ background: `${color}14`, borderBottom: `2px solid ${color}` }}>
                  <span className="w-3 h-3 rounded-full" style={{ background: color }} />
                  {r.name.replace(`${company} — `, "")}
                  {onRename && (
                    <button
                      data-testid={`route-rename-${r.id}`}
                      title="Güzergah adını değiştir"
                      onClick={() => {
                        const yeni = window.prompt("Yeni güzergah adı:", r.name);
                        if (yeni && yeni.trim() && yeni.trim() !== r.name) onRename(r.id, yeni.trim());
                      }}
                      className="p-0.5 opacity-50 hover:opacity-100"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                  <span className="ml-auto num font-semibold text-neutral-500">{r.estimated_km > 0 ? `${r.estimated_km} km` : ""}</span>
                </div>
                <div className="px-3 py-2 space-y-0.5">
                  {(r.stops || []).map((s, si) =>
                    (s.personnel || []).map((p) => (
                      <div key={p.id} className="flex items-center gap-2 text-xs py-0.5">
                        <button
                          data-testid={`list-locate-${r.id}-${p.id}`}
                          onClick={() => locatePerson(r.id, si, s.lat, s.lng)}
                          title="Haritada göster"
                          className="flex items-center gap-1.5 min-w-0 flex-1 text-left hover:text-[#0020C2] hover:underline"
                        >
                          <span className="num text-neutral-400 shrink-0">{si + 1}.</span>
                          <span className="truncate">{p.name}</span>
                          <MapPin className="w-3 h-3 shrink-0 opacity-40" />
                        </button>
                        <span className="num text-neutral-500 shrink-0">{p.pickup_time || ""}</span>
                        {p.walk_m > 0 && <span className="num text-[10px] text-neutral-400 shrink-0">{p.walk_m} m</span>}
                        {onMove && mapRoutes.length > 1 && (
                          <select
                            data-testid={`list-move-${r.id}-${p.id}`}
                            defaultValue=""
                            onChange={(e) => { if (e.target.value) onMove(r.id, p.id, e.target.value); }}
                            title="Bu personeli başka güzergaha taşı"
                            className="text-[10px] border border-[#E5E5E5] bg-white px-1 py-0.5 cursor-pointer text-[#0020C2] font-semibold shrink-0 max-w-[110px]"
                          >
                            <option value="">Değiştir →</option>
                            {mapRoutes.filter((x) => x.id !== r.id).map((x) => (
                              <option key={x.id} value={x.id}>{x.name.replace(`${company} — `, "").slice(0, 35)}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
